import { useState, useMemo } from 'react';
import './ToolStyles.css';

type UnitCategory = 'length' | 'weight' | 'temperature' | 'area' | 'speed';

interface UnitDef { toBase: (v: number) => number; fromBase: (v: number) => number; }

const UNITS: Record<UnitCategory, Record<string, UnitDef>> = {
    length: {
        '米 (m)': { toBase: (v) => v, fromBase: (v) => v },
        '千米 (km)': { toBase: (v) => v * 1000, fromBase: (v) => v / 1000 },
        '厘米 (cm)': { toBase: (v) => v / 100, fromBase: (v) => v * 100 },
        '毫米 (mm)': { toBase: (v) => v / 1000, fromBase: (v) => v * 1000 },
        '英尺 (ft)': { toBase: (v) => v * 0.3048, fromBase: (v) => v / 0.3048 },
        '英寸 (in)': { toBase: (v) => v * 0.0254, fromBase: (v) => v / 0.0254 },
    },
    weight: {
        '千克 (kg)': { toBase: (v) => v, fromBase: (v) => v },
        '克 (g)': { toBase: (v) => v / 1000, fromBase: (v) => v * 1000 },
        '毫克 (mg)': { toBase: (v) => v / 1e6, fromBase: (v) => v * 1e6 },
        '磅 (lb)': { toBase: (v) => v * 0.453592, fromBase: (v) => v / 0.453592 },
        '盎司 (oz)': { toBase: (v) => v * 0.0283495, fromBase: (v) => v / 0.0283495 },
        '斤': { toBase: (v) => v * 0.5, fromBase: (v) => v / 0.5 },
    },
    temperature: {
        '摄氏度 (°C)': {
            toBase: (v) => v,
            fromBase: (v) => v,
        },
        '华氏度 (°F)': {
            toBase: (v) => (v - 32) * 5 / 9,
            fromBase: (v) => v * 9 / 5 + 32,
        },
        '开尔文 (K)': {
            toBase: (v) => v - 273.15,
            fromBase: (v) => v + 273.15,
        },
    },
    area: {
        '平方米 (m²)': { toBase: (v) => v, fromBase: (v) => v },
        '平方千米 (km²)': { toBase: (v) => v * 1e6, fromBase: (v) => v / 1e6 },
        '公顷 (ha)': { toBase: (v) => v * 10000, fromBase: (v) => v / 10000 },
        '亩': { toBase: (v) => v * 666.667, fromBase: (v) => v / 666.667 },
        '平方英尺 (ft²)': { toBase: (v) => v * 0.092903, fromBase: (v) => v / 0.092903 },
    },
    speed: {
        '米/秒 (m/s)': { toBase: (v) => v, fromBase: (v) => v },
        '千米/时 (km/h)': { toBase: (v) => v / 3.6, fromBase: (v) => v * 3.6 },
        '英里/时 (mph)': { toBase: (v) => v * 0.44704, fromBase: (v) => v / 0.44704 },
        '节 (kn)': { toBase: (v) => v * 0.514444, fromBase: (v) => v / 0.514444 },
    },
};

const CATEGORY_LABELS: Record<UnitCategory, string> = {
    length: '长度', weight: '重量', temperature: '温度', area: '面积', speed: '速度',
};

const UnitConverter = () => {
    const [cat, setCat] = useState<UnitCategory>('length');
    const [value, setValue] = useState('');
    const [fromUnit, setFromUnit] = useState(Object.keys(UNITS.length)[0]);
    const [toUnit, setToUnit] = useState(Object.keys(UNITS.length)[1]);

    const result = useMemo(() => {
        const num = parseFloat(value);
        if (isNaN(num) || value === '') return null;
        const base = UNITS[cat][fromUnit].toBase(num);
        const converted = UNITS[cat][toUnit].fromBase(base);
        return Number(converted.toFixed(6)).toString();
    }, [cat, value, fromUnit, toUnit]);

    const handleCatChange = (newCat: UnitCategory) => {
        setCat(newCat);
        const keys = Object.keys(UNITS[newCat]);
        setFromUnit(keys[0]);
        setToUnit(keys[1] || keys[0]);
    };

    return (
        <div className="tool-container">
            <div className="button-group">
                {(Object.keys(UNITS) as UnitCategory[]).map((c) => (
                    <button
                        key={c}
                        className={`mode-button ${cat === c ? 'active' : ''}`}
                        onClick={() => handleCatChange(c)}
                    >
                        {CATEGORY_LABELS[c]}
                    </button>
                ))}
            </div>

            <div className="input-group">
                <label className="input-label">数值</label>
                <input
                    className="tool-input"
                    type="number"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="输入数值..."
                />
            </div>

            <div className="input-group">
                <label className="input-label">源单位</label>
                <select className="tool-select" value={fromUnit}
                    onChange={(e) => setFromUnit(e.target.value)}>
                    {Object.keys(UNITS[cat]).map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
            </div>

            <div className="input-group">
                <label className="input-label">目标单位</label>
                <select className="tool-select" value={toUnit}
                    onChange={(e) => setToUnit(e.target.value)}>
                    {Object.keys(UNITS[cat]).map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
            </div>

            {result !== null && (
                <div className="tool-output large" style={{ textAlign: 'center' }}>
                    {value} {fromUnit.split(' ')[0]} = {result} {toUnit.split(' ')[0]}
                </div>
            )}
        </div>
    );
};

export default UnitConverter;
